const MAX_HISTORY = 1000;

// ── History ───────────────────────────────────────────────────────────────────

const normalizeHistoryEntries = (entries) =>
  (entries || []).map((e) => (typeof e === "string" ? { word: e, timestamp: 0 } : e));

/**
 * Normalise any history content (v1 bare array, or v2 { entries, deleted }) to the
 * canonical { entries, deleted } shape so a deletion can be represented on Drive.
 */
export const normalizeHistoryPayload = (payload) => {
  if (Array.isArray(payload)) return { entries: normalizeHistoryEntries(payload), deleted: {} };
  if (payload && payload.schemaVersion === 2) {
    return { entries: normalizeHistoryEntries(payload.entries), deleted: payload.deleted || {} };
  }
  return { entries: [], deleted: {} };
};

/**
 * Merge local history against a remote payload, honouring tombstones so that a
 * deletion (removeHistoryItem / clearHistory) propagates and is not resurrected.
 *
 * A word is suppressed while its effective deletedAt (max of local + remote
 * tombstones) is >= the newest surviving search timestamp for that word; a later
 * re-search (timestamp > deletedAt) brings it back and retires the tombstone.
 *
 * @param {Array}  local            local history entries (may be legacy strings)
 * @param {*}      remotePayload     Drive content (v1 array or v2 payload)
 * @param {Object} localTombstones  { [word]: deletedAt }
 * @returns {{ entries, tombstones }}
 */
export const mergeHistory = (local, remotePayload, localTombstones = {}) => {
  const remote = normalizeHistoryPayload(remotePayload);
  const localNorm = normalizeHistoryEntries(local);

  // LWW per word (later timestamp wins; local wins an exact tie).
  const map = new Map();
  for (const e of remote.entries) map.set(e.word, e);
  for (const e of localNorm) {
    const existing = map.get(e.word);
    if (!existing || e.timestamp >= existing.timestamp) map.set(e.word, e);
  }

  const tombstones = { ...localTombstones };
  for (const [word, ts] of Object.entries(remote.deleted)) {
    if (!tombstones[word] || ts > tombstones[word]) tombstones[word] = ts;
  }

  const entries = [];
  for (const e of map.values()) {
    const del = tombstones[e.word] ?? 0;
    // Tombstone wins on a tie (delete beats a same-instant search) — matches the
    // bookmark/flashcard suppression rule.
    if (del > 0 && del >= e.timestamp) continue;
    if (tombstones[e.word]) delete tombstones[e.word]; // survivor retires its tombstone
    entries.push(e);
  }
  entries.sort((a, b) => b.timestamp - a.timestamp);

  return { entries: entries.slice(0, MAX_HISTORY), tombstones };
};

// ── Bookmarks ─────────────────────────────────────────────────────────────────

/**
 * Normalise a v1 bookmarks payload (plain { word: definitions[] }) into v2 shape.
 */
const normalizeBookmarksPayload = (payload) => {
  if (payload && payload.schemaVersion === 2) return payload;
  // v1: every value is the definitions array
  const items = {};
  for (const [word, def] of Object.entries(payload || {})) {
    items[word] = { def, addedAt: 0, lastModified: 0 };
  }
  return { schemaVersion: 2, items, deleted: {} };
};

/**
 * Merge local bookmarks state + metadata against a remote Drive payload.
 *
 * @param {Object} localItems       { [word]: definitions[] }
 * @param {Object} localMeta        { [word]: { addedAt, lastModified } }
 * @param {Object} localTombstones  { [word]: deletedAt }
 * @param {Object} remotePayload    Drive payload (v1 or v2)
 * @returns {{ items, meta, tombstones }}
 */
export const mergeBookmarks = (localItems, localMeta, localTombstones, remotePayload) => {
  const remote = normalizeBookmarksPayload(remotePayload);

  const allWords = new Set([
    ...Object.keys(localItems || {}),
    ...Object.keys(remote.items || {}),
    ...Object.keys(localTombstones || {}),
    ...Object.keys(remote.deleted || {}),
  ]);

  const mergedItems = {};
  const mergedMeta = {};
  const mergedTombstones = { ...localTombstones };

  for (const word of allWords) {
    const localTs = localMeta?.[word]?.lastModified ?? 0;
    const remoteTs = remote.items?.[word]?.lastModified ?? 0;
    const localDeletedAt = localTombstones?.[word] ?? 0;
    const remoteDeletedAt = remote.deleted?.[word] ?? 0;
    const effectiveDeletedAt = Math.max(localDeletedAt, remoteDeletedAt);

    // Tombstone wins if the deletion is newer than the latest write
    if (effectiveDeletedAt > 0 && effectiveDeletedAt >= Math.max(localTs, remoteTs)) {
      mergedTombstones[word] = effectiveDeletedAt;
      continue;
    }
    // Survivor: drop any stale tombstone so a re-add isn't perpetually deleted.
    if (mergedTombstones[word]) delete mergedTombstones[word];

    const hasLocal = word in (localItems || {});
    const hasRemote = word in (remote.items || {});

    if (hasLocal && hasRemote) {
      // Newer lastModified wins; on an exact tie, deterministic by canonical def
      // (the same choice on every device → convergence on concurrent equal edits).
      const localWins =
        localTs !== remoteTs
          ? localTs > remoteTs
          : JSON.stringify(localItems[word]) >= JSON.stringify(remote.items[word].def);
      if (localWins) {
        mergedItems[word] = localItems[word];
        mergedMeta[word] = localMeta?.[word] ?? { addedAt: localTs, lastModified: localTs };
      } else {
        mergedItems[word] = remote.items[word].def;
        mergedMeta[word] = {
          addedAt: remote.items[word].addedAt ?? remoteTs,
          lastModified: remoteTs,
        };
      }
    } else if (hasLocal) {
      mergedItems[word] = localItems[word];
      mergedMeta[word] = localMeta?.[word] ?? { addedAt: 1, lastModified: 1 };
    } else if (hasRemote) {
      mergedItems[word] = remote.items[word].def;
      mergedMeta[word] = {
        addedAt: remote.items[word].addedAt ?? remoteTs,
        lastModified: remoteTs,
      };
    }
  }

  return { items: mergedItems, meta: mergedMeta, tombstones: mergedTombstones };
};

// ── Flashcard collections ──────────────────────────────────────────────────────

const cardLM = (c) => c?.lastModified ?? c?.addedAt ?? 0;

// Device-independent canonical form for deterministic tie-breaks.
const cardCanon = (c) =>
  JSON.stringify({ w: c?.word, d: c?.definition, r: c?.root, id: c?.wordID });

// LWW with a deterministic tie-break: newer lastModified wins; on an exact tie,
// the lexicographically-larger canonical form wins — the SAME choice on every
// device, so two replicas that concurrently edit to different values converge.
const pickCard = (lc, rc, lcTs = cardLM(lc), rcTs = cardLM(rc)) => {
  if (lcTs !== rcTs) return lcTs > rcTs ? lc : rc;
  return cardCanon(lc) >= cardCanon(rc) ? lc : rc;
};

const normalizeCard = (card, fallbackTs = 0) => ({
  ...card,
  addedAt: card.addedAt ?? fallbackTs,
  lastModified: card.lastModified ?? card.addedAt ?? fallbackTs,
});

/**
 * Convert v1 monolithic flashcards map to a synthetic per-collection payload map
 * and a collections index, assigning UUIDs from the provided idMap (a name → id Map,
 * mutated in place).
 */
const normalizeV1Flashcards = (v1Map, idMap, mintId) => {
  if (!v1Map || typeof v1Map !== "object") return { index: null, payloads: {} };

  const collections = [];
  const payloads = {};

  for (const [name, cards] of Object.entries(v1Map)) {
    if (!idMap.get(name)) idMap.set(name, mintId());
    const id = idMap.get(name);
    const normalizedCards = (cards || []).map((c) => normalizeCard(c, 0));
    const lastModified = normalizedCards.reduce((max, c) => Math.max(max, cardLM(c)), 0);
    payloads[id] = { id, name, cards: normalizedCards, lastModified, deleted: {}, schemaVersion: 2 };
    collections.push({ id, name, lastModified });
  }

  return { index: { schemaVersion: 2, collections, deleted: {} }, payloads };
};

/**
 * Merge local flashcard collections against remote Drive data.
 *
 * Collections are merged by stable id. Per-card deletions propagate through each
 * collection file's `deleted` map (so a deletion on one device is honoured on the
 * others) and through collection-scoped local tombstones. Two collections that
 * resolve to the SAME name (e.g. independently created on two devices) are unioned
 * into one surviving id rather than silently dropping one.
 *
 * @returns {{ collections, collectionIdMap, collectionMeta, flashcardTombstones,
 *             collectionTombstones, orphanCollectionIds, mergedInto }}
 *   mergedInto maps each id folded into a same-named survivor to that survivor's id.
 */
export const mergeCollections = (
  localCollections,
  localIdMap,
  localFcTombstones,
  localColTombstones,
  remoteIndex,
  remotePayloads,
  localCollectionMeta = {},
  mintId = () => Math.random().toString(36).slice(2)
) => {
  // Name-keyed state lives in Maps: a collection may be named "constructor" or
  // "__proto__", which a plain-object lookup resolves to Object.prototype (and an
  // assignment to "__proto__" swaps the prototype instead of adding a key).
  const idMap = new Map(Object.entries(localIdMap || {}));
  let resolvedIndex = remoteIndex;
  let resolvedPayloads = { ...remotePayloads };

  // V1 detection: no schemaVersion on the index ⇒ old monolithic flashcards.json
  const isV1Remote =
    remoteIndex && typeof remoteIndex === "object" && !remoteIndex.schemaVersion;
  if (isV1Remote) {
    const { index, payloads } = normalizeV1Flashcards(remoteIndex, idMap, mintId);
    resolvedIndex = index;
    resolvedPayloads = payloads;
  }

  // Ensure every local collection has an id.
  for (const name of Object.keys(localCollections || {})) {
    if (!idMap.get(name)) idMap.set(name, mintId());
  }

  // Local collections indexed by id.
  const localById = {};
  for (const [name, cards] of Object.entries(localCollections || {})) {
    const id = idMap.get(name);
    if (!id) continue;
    const normalizedCards = (cards || []).map((c) => normalizeCard(c, 0));
    const metaLM = localCollectionMeta?.[id]?.lastModified ?? 0;
    const lastModified = normalizedCards.reduce((m, c) => Math.max(m, cardLM(c)), metaLM);
    localById[id] = { id, name, cards: normalizedCards, lastModified };
  }

  const allCollectionIds = new Set([
    ...Object.keys(localById),
    ...(resolvedIndex?.collections || []).map((c) => c.id),
    ...Object.keys(resolvedPayloads),
  ]);

  // Intermediate per-id merge result (before name-collision resolution).
  const byId = {}; // id → { id, name, cards[], lastModified }
  const mergedFcTombstones = JSON.parse(JSON.stringify(localFcTombstones || {}));
  const mergedColTombstones = { ...localColTombstones };
  const mergedColMeta = {};
  const orphanCollectionIds = new Set();
  const mergedInto = new Map();

  for (const collectionId of allCollectionIds) {
    const local = localById[collectionId];
    const remote = resolvedPayloads[collectionId];
    const remoteIdxEntry = (resolvedIndex?.collections || []).find((c) => c.id === collectionId);

    // Guard: an index entry whose payload failed to fetch and isn't local.
    if (!local && !remote && !remoteIdxEntry) continue;

    const localDeletedAt = localColTombstones?.[collectionId] ?? 0;
    const remoteDeletedAt = resolvedIndex?.deleted?.[collectionId] ?? 0;
    const effectiveDeletedAt = Math.max(localDeletedAt, remoteDeletedAt);

    const localLM = local?.lastModified ?? 0;
    const remoteLM = remote?.lastModified ?? remoteIdxEntry?.lastModified ?? 0;

    // Collection tombstone wins if the deletion is newer than any content.
    if (effectiveDeletedAt > 0 && effectiveDeletedAt >= Math.max(localLM, remoteLM)) {
      mergedColTombstones[collectionId] = effectiveDeletedAt;
      orphanCollectionIds.add(collectionId); // its Drive file should be removed
      continue;
    }
    // Survivor: clear any stale collection tombstone (re-created after delete).
    if (mergedColTombstones[collectionId]) delete mergedColTombstones[collectionId];

    // If neither side has content but the index referenced it (payload fetch
    // failed), skip without crashing — it'll be retried next sync.
    if (!local && !remote) continue;

    // Newer collection lastModified wins the name; on a tie, deterministic by name.
    let canonicalName;
    if (local && remote) {
      if (localLM !== remoteLM) canonicalName = localLM > remoteLM ? local.name : remote.name;
      else canonicalName = local.name >= remote.name ? local.name : remote.name;
    } else {
      canonicalName = local ? local.name : remote.name;
    }

    const localCards = local?.cards || [];
    const remoteCards = remote?.cards || [];
    const remoteDeleted = remote?.deleted || {};

    const allWordIDs = new Set([
      ...localCards.map((c) => c.wordID),
      ...remoteCards.map((c) => c.wordID),
      ...Object.keys(remoteDeleted),
    ]);

    const localCardMap = new Map(localCards.map((c) => [c.wordID, c]));
    const remoteCardMap = new Map(remoteCards.map((c) => [c.wordID, c]));

    const mergedCards = [];
    for (const wordID of allWordIDs) {
      const lc = localCardMap.get(wordID);
      const rc = remoteCardMap.get(wordID);
      const lcTs = cardLM(lc);
      const rcTs = cardLM(rc);

      // DURABLE deletion = collection-scoped local tombstone + remote file's
      // deleted map. The legacy "__global" bucket is treated as a LOCAL-ONLY,
      // best-effort suppression that must NEVER be shipped into the per-collection
      // file (otherwise one device's legacy deletion wrongly deletes the card on
      // every other device, and across unrelated collections).
      const scopedLocalDel = localFcTombstones?.[collectionId]?.[wordID] ?? 0;
      const globalDel = localFcTombstones?.__global?.[wordID] ?? 0;
      const remoteDel = remoteDeleted[wordID] ?? 0;
      const durableDel = Math.max(scopedLocalDel, remoteDel);
      const effDel = Math.max(durableDel, globalDel);

      if (effDel > 0 && effDel >= Math.max(lcTs, rcTs)) {
        // Card is suppressed. Only RECORD (and thus ship) a tombstone when the
        // deletion is durable; a global-only suppression stays local.
        if (durableDel > 0) {
          if (!mergedFcTombstones[collectionId]) mergedFcTombstones[collectionId] = {};
          mergedFcTombstones[collectionId][wordID] = durableDel;
        }
        continue;
      }
      // Survivor: drop stale per-card tombstones (scoped AND legacy global) so a
      // re-add / newer peer card isn't perpetually re-killed.
      if (mergedFcTombstones[collectionId]?.[wordID]) delete mergedFcTombstones[collectionId][wordID];
      if (mergedFcTombstones.__global?.[wordID]) delete mergedFcTombstones.__global[wordID];

      if (lc && rc) mergedCards.push(pickCard(lc, rc, lcTs, rcTs));
      else if (lc) mergedCards.push(lc);
      else if (rc) mergedCards.push(rc);
    }

    byId[collectionId] = {
      id: collectionId,
      name: canonicalName,
      cards: mergedCards,
      lastModified: Math.max(localLM, remoteLM),
    };
  }

  // ── Resolve same-name collisions: union cards into one surviving id ──────────
  const byName = new Map();
  for (const entry of Object.values(byId)) {
    if (!byName.has(entry.name)) byName.set(entry.name, []);
    byName.get(entry.name).push(entry);
  }

  const collections = new Map();
  const collectionIdMap = new Map();
  for (const [name, group] of byName) {
    if (group.length === 1) {
      const e = group[0];
      collections.set(name, e.cards);
      collectionIdMap.set(name, e.id);
      mergedColMeta[e.id] = { lastModified: e.lastModified };
      continue;
    }
    // Deterministic survivor across devices: lexicographically smallest id.
    const sorted = [...group].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const survivor = sorted[0];
    const losers = sorted.slice(1);

    // 1. Carry every loser's card tombstones onto the survivor FIRST, so the
    //    union below can honour them (no resurrection of a deleted card).
    mergedFcTombstones[survivor.id] = { ...(mergedFcTombstones[survivor.id] || {}) };
    const survivorTombs = mergedFcTombstones[survivor.id];
    for (const loser of losers) {
      for (const [wid, ts] of Object.entries(mergedFcTombstones[loser.id] || {})) {
        if (ts > (survivorTombs[wid] ?? 0)) survivorTombs[wid] = ts;
      }
    }

    // 2. Union cards (deterministic LWW), then reconcile against the combined
    //    tombstones so the published file is self-consistent in one pass.
    const unionMap = new Map();
    let maxLM = survivor.lastModified;
    for (const e of sorted) {
      maxLM = Math.max(maxLM, e.lastModified);
      for (const c of e.cards) {
        const prev = unionMap.get(c.wordID);
        unionMap.set(c.wordID, prev ? pickCard(prev, c) : c);
      }
    }
    const unionCards = [];
    for (const c of unionMap.values()) {
      const del = survivorTombs[c.wordID] ?? 0;
      if (del > 0 && del >= cardLM(c)) continue; // tombstoned newer than the card
      delete survivorTombs[c.wordID]; // card survives → clear stale tombstone
      unionCards.push(c);
    }

    collections.set(name, unionCards);
    collectionIdMap.set(name, survivor.id);
    mergedColMeta[survivor.id] = { lastModified: maxLM };

    // 3. Retire each loser id by orphaning its FILE only — deliberately NOT via a
    //    collection tombstone. A collection tombstone means "this collection was
    //    deleted, drop its cards", and the per-id loop above honours it by dropping
    //    the whole collection *before* this union runs. That is correct for a user
    //    delete but catastrophic for a same-name collapse: a partitioned peer that
    //    later shows up with cards under the loser id (e.g. a card MOVED into it)
    //    would have them suppressed by the tombstone and permanently lost. The
    //    by-name union here is idempotent and runs on every device every round, so
    //    each peer re-collapses the loser into the survivor and migrates its cards;
    //    no tombstone is needed to keep the loser from resurrecting.
    for (const loser of losers) {
      orphanCollectionIds.add(loser.id);
      mergedInto.set(loser.id, survivor.id);
    }
  }

  return {
    // fromEntries defines own keys, so even "__proto__" comes out as an ordinary key.
    collections: Object.fromEntries(collections),
    collectionIdMap: Object.fromEntries(collectionIdMap),
    collectionMeta: mergedColMeta,
    flashcardTombstones: mergedFcTombstones,
    collectionTombstones: mergedColTombstones,
    orphanCollectionIds: [...orphanCollectionIds],
    mergedInto: Object.fromEntries(mergedInto),
  };
};
